import {get, startsWith, toNumber} from 'lodash';
import {createHashHistory} from 'history';

import {isAdmin, isPublisherUserOrPublisherAdmin, isPublisherDSP, isValidPublisherId, getPublisherIdList} from '../auth';
import {loadFromLocalStorage} from './../utils/localStorage';

const history = createHashHistory();
const hash = window.location.hash;

// segment[0] = #
// segment[1] = publisher ID, or the literal "internal" on a Supply Manager deep link
// segment[2] = sidebar or account route, or the publisher ID when segment[1] is "internal"

/*
    Sidebar routes (auth)
        - dashboard
        - placements
        - supply
        - analytics
**/

/*
    Account routes (public)
        - / (login)
        - password-set
        - password-reset
        - create-account
        - welcome-back
        - activation-email-sent
**/

// Supply Manager deep links arrive as #/internal/:publisherId/:page and are only rewritten to
// #/:publisherId/:page once AppRouter renders its <Redirect>. Read the segments live and offset
// past the prefix so the answer does not depend on whether that render has happened yet
// (UN-54784 Flow B).
const getHashSegments = () => window.location.hash.split('/');
const getPublisherIdIndex = (segments) => (segments[1] === 'internal' ? 2 : 1);

export const getPublisherId = () => {
    const segments = getHashSegments();
    return toNumber(segments[getPublisherIdIndex(segments)]);
};

export const redirectToLoginPage = () => {
    history.replace('/');
};

export const redirectTo = (route = '/') => {
    history.replace(route);
};

export const redirectToHomePage = () => {
    const user = loadFromLocalStorage('user');

    // Guard: user null/undefined (empty localStorage) — redirect to login root.
    if (!user) {
        return history.push('/');
    }

    const publisherId = isValidPublisherId(get(user, 'currentPublisherId'))
        ? get(user, 'currentPublisherId')
        : null;

    const allowedList = getPublisherIdList(get(user, 'allowedPublisherId'));
    const isUsable = !!publisherId && allowedList.includes(String(publisherId));

    const publisherIdUrl = getPublisherId();
    const segments = getHashSegments();
    const sidebarUrl = segments[getPublisherIdIndex(segments) + 1];

    // Reset after setting new password, auto login in the background
    if (startsWith(hash, 'password-set', 2)) {
        history.push('/');
    }

    // Publisher user/admin with no usable current publisher (missing, zero, not in allowed list):
    // redirect to home where Home.js will show the picker or the no-allowed-publisher message.
    // Undertone internals also carry PUBLISHER_ADMIN (role 4) but have no publisher claims at
    // all, so isAdmin must win here — otherwise an internal following a /internal/:id deep link
    // is bounced to "/" and the id in the URL is discarded (UN-54784 Flow B).
    if (!isAdmin(user) && isPublisherUserOrPublisherAdmin(user) && !isUsable) {
        return history.push('/');
    }
    // Display default publisher for publisher DSP users
    if (isPublisherDSP(user) && publisherId) {
        if (sidebarUrl) {
            return history.push(`/${publisherId}/${sidebarUrl}`);
        }
        return history.push(`/${publisherId}/placements`);
    }
    // Display default publisher for direct users
    if (isPublisherUserOrPublisherAdmin(user) && publisherId) {
        if (sidebarUrl) {
            return history.push(`/${publisherId}/${sidebarUrl}`);
        }
        return history.push(`/${publisherId}/dashboard`);
    }

    // Reset URL if non-admin users try to modify the publisher ID.
    // publisherId may be null at this point (unrecognised role, DSP without valid current).
    // Never interpolate a null publisher id into a path.
    if (!isAdmin(user) && publisherIdUrl !== publisherId) {
        if (!publisherId) {
            return history.push('/');
        }
        if (sidebarUrl) {
            return history.push(`/${publisherId}/${sidebarUrl}`);
        }
        return history.push(`/${publisherId}/dashboard`);
    }

    // Display select publisher view as home page for admins (no publisher in URL or session)
    if (!publisherId && !publisherIdUrl && isAdmin(user)) {
        return history.push('/');
    }

    // Resolve effective id for remaining cases (primarily admins).
    // Admins may have no currentPublisherId in session but may be viewing a publisher URL —
    // use the id already in the URL rather than interpolating null.
    const effectiveId = publisherId || (isAdmin(user) ? publisherIdUrl : null);
    if (!effectiveId) {
        // No usable publisher id for any remaining case: redirect to root.
        return history.push('/');
    }

    // Display dashboard as home page
    if (hash === '' || hash === '#/') {
        return history.push(`/${effectiveId}/dashboard`);
    }
    if (!sidebarUrl || sidebarUrl === '') {
        return history.replace(`/${effectiveId}/dashboard`);
    }
};